import superFetch from 'library/helpers/superFetch';
import { DeviceGroupsIcon, DevicesIcon } from 'assets/images/svg';
import { TeamIcon, UserIcon } from 'assets/images/svg';
import { CREDENTIAL_TYPE_PASSCODE } from 'config/credentialTypes';
import { entityType } from 'config/entityType';
import { INTEGRATIONS_TYPES } from 'config/integrationTypes';
import { constants } from 'config/constants';

export const getAccessData = async ({ teamID, personID, deviceID, assetGroupID }) => {
	if (teamID) {
		const teamAccessData = await superFetch.get(`/access`, {}, { teamID: teamID }).then((res) => {
			if (res.statusCode === 200) return res.data;
			return [];
		});
		return teamAccessData;
	} else if (personID) {
		const personAccessData = await superFetch.get(`/access`, {}, { personID: personID }).then((res) => {
			if (res.statusCode === 200) return res.data;
			return [];
		});
		return personAccessData;
	} else if (deviceID) {
		const lockAccessData = await superFetch.get(`/access`, {}, { deviceID: deviceID }).then((res) => {
			if (res.statusCode === 200) return res.data;
			return [];
		});
		return lockAccessData;
	} else if (assetGroupID) {
		const lockAccessData = await superFetch.get(`/access`, {}, { assetGroupID: assetGroupID }).then((res) => {
			if (res.statusCode === 200) return res.data;
			return [];
		});
		return lockAccessData;
	}
};

/**
 * Specialized grouping for the Team Permissions Panel (Ticket #4681).
 * Focuses on distinguishing between Direct Device access and Asset Group access.
 */
export function groupPermissionsByTeam({ permissions, devices, deviceGroups, teams, currentTeamID }) {
	// 🔐 Pre-filter permissions to only process entities within permitted scope
	const permittedDeviceIDs = new Set(devices.map((d) => d.deviceID));
	const permittedGroupIDs = new Set(deviceGroups.map((g) => g.groupID));

	const scopedPermissions = permissions.filter((perm) => {
		if (perm.assetGroupID) return permittedGroupIDs.has(perm.assetGroupID);
		if (perm.deviceID) return permittedDeviceIDs.has(perm.deviceID);
		return false;
	});

	const grouped = {};

	scopedPermissions.forEach((perm) => {
		const isGroup = !!perm.assetGroupID;

		// 🔥 UNIQUE KEY: Differentiate by Device or Group.
		// We do not collapse these because an admin needs to see both sources
		// for confident auditing.
		const key = isGroup ? `group-${perm.assetGroupID}` : `device-${perm.deviceID}`;

		if (!grouped[key]) {
			grouped[key] = {
				permission: perm,
				deviceIDs: new Set()
			};
		}

		if (perm.deviceID) {
			grouped[key].deviceIDs.add(perm.deviceID);
		}
	});

	return Object.values(grouped).map(({ permission, deviceIDs }) => {
		const group = deviceGroups.find((g) => g.groupID === permission.assetGroupID);
		const device = devices.find((d) => d.deviceID === permission.deviceID);

		// For groups, resolve devices from the group's device list
		// For direct devices, use the aggregated deviceIDs from permissions
		const resolvedDeviceIDs = permission.assetGroupID && group?.devices ? group.devices : [...deviceIDs];

		const deviceList = resolvedDeviceIDs
			.map((id) => {
				const d = devices.find((dev) => dev.deviceID === id);
				return d ? { deviceID: d.deviceID, deviceName: d.name } : null;
			})
			.filter(Boolean);

		let label = '';
		let tooltip = '';
		let badgeColor = 'default';
		let Icon = DevicesIcon;

		// Visual distinction and source metadata
		if (permission.assetGroupID) {
			badgeColor = 'blue';
			Icon = DeviceGroupsIcon;
			label = group?.name || 'Unknown Group';
			tooltip = 'Via Group';
		} else {
			badgeColor = 'gold';
			Icon = DevicesIcon;
			label = device?.name || 'Unknown Device';
			tooltip = 'Direct';
		}

		return {
			permissionID: permission.permissionID,
			entityTypeID: permission.entityTypeID,
			entityID: permission.entityID,
			assetGroupID: permission.assetGroupID || null,
			deviceNames: deviceList.map((d) => d.deviceName),
			label,
			tooltip,
			badgeColor,
			Icon
		};
	});
}

/**
 * Specialized grouping for the Person Permissions Panel (Ticket #4681).
 * Focuses on auditability by distinguishing between Direct, Team, and Group access.
 */
export function groupPermissionsBySourceForPerson({ permissions, devices, deviceGroups, teams }) {
	// 🔐 Pre-filter permissions to only process entities within permitted scope
	const permittedDeviceIDs = new Set(devices.map((d) => d.deviceID));
	const permittedGroupIDs = new Set(deviceGroups.map((g) => g.groupID));
	const permittedTeamIDs = new Set(teams.map((t) => t.teamID));

	const scopedPermissions = permissions.filter((perm) => {
		if (perm.assetGroupID) return permittedGroupIDs.has(perm.assetGroupID);
		if (perm.deviceID) return permittedDeviceIDs.has(perm.deviceID);
		if (perm.teamID) return permittedTeamIDs.has(perm.teamID);
		return false;
	});

	const grouped = {};

	scopedPermissions.forEach((perm) => {
		const isGroup = !!perm.assetGroupID;

		// 🔥 UNIQUE KEY: Differentiate by hardware entity AND access source.
		// This prevents the UI from collapsing Direct access and Team access into one tag.
		const key = isGroup
			? `group-${perm.assetGroupID}-${perm.teamID || 'direct'}`
			: `device-${perm.deviceID}-${perm.teamID || 'direct'}`;

		if (!grouped[key]) {
			grouped[key] = {
				permission: perm,
				deviceIDs: new Set()
			};
		}

		// Aggregate individual device IDs for the "Includes: ..." tooltip section
		if (perm.deviceID) {
			grouped[key].deviceIDs.add(perm.deviceID);
		}
	});

	return Object.values(grouped).map(({ permission, deviceIDs }) => {
		// Hydrate device names for tooltip transparency
		const deviceList = [...deviceIDs]
			.map((id) => {
				const d = devices.find((dev) => dev.deviceID === id);
				return d ? { deviceID: d.deviceID, deviceName: d.name } : null;
			})
			.filter(Boolean);

		const team = teams.find((t) => t.teamID === permission.teamID);
		const group = deviceGroups.find((g) => g.groupID === permission.assetGroupID);
		const device = devices.find((d) => d.deviceID === permission.deviceID);

		let label = '';
		let tooltip = '';
		let badgeColor = 'default';
		let Icon = DevicesIcon; // Standard Device Icon

		// UI styling based on the type of permission
		if (permission.assetGroupID) {
			badgeColor = 'blue';
			Icon = DeviceGroupsIcon;
			label = group?.name || 'Unknown Group';
		} else {
			badgeColor = 'gold';
			Icon = DevicesIcon;
			label = device?.name || 'Unknown Device';
		}

		if (permission.teamID) {
			tooltip = `Via ${team?.name || 'Team'}`;
		} else {
			tooltip = 'Direct';
		}

		return {
			...permission,
			permissionID: permission.permissionID,
			entityTypeID: permission.entityTypeID,
			entityID: permission.entityID,
			label,
			tooltip,
			badgeColor,
			Icon,
			deviceNames: deviceList.map((d) => d.deviceName)
		};
	});
}

/**
 * Specialized grouping for the Access Card Panel on the Person Drawer.
 * Shows which devices a person can access via physical card assignment.
 */
export function groupCardAccessBySourceForPerson({ credentialAccess = [], credentials = [], devices = [] }) {
	return credentialAccess
		.filter((ca) => ca.personID !== null && ca.deviceID)
		.map((ca) => {
			const credential = credentials.find((c) => c.credentialID === ca.credentialID);
			const device = devices.find((d) => d.deviceID === ca.deviceID);
			return {
				key: `ca-${ca.credentialID}-${ca.deviceID}`,
				label: device?.name || 'Unknown Device',
				tooltip: `Access via Card: ${credential?.name || 'Unknown Card'}`,
				badgeColor: 'volcano',
				credentialID: ca.credentialID,
				deviceID: ca.deviceID
			};
		});
}

/**
 * Groups a flat list of credentials by their physical identifier (ExternalValue).
 * Credentials sharing the same ExternalValue represent the same physical credential
 * (e.g. a card with the same CSN, or a pincode with the same external system ID)
 * registered across multiple integrations.
 *
 * Each group exposes an `integrations` array so the frontend knows exactly which
 * integrations the credential exists in and which credentialID to use per action.
 *
 * Credentials without an ExternalValue are treated as standalone — one row each.
 * IR integration is used as the primary record when present.
 *
 * @param {Object[]} credentials - Flat array of credential records from DB.
 * @returns {Object[]} Array of grouped credential records with integrations field.
 */
export function groupCredentialsByPhysicalIdentifier(credentials) {
	const grouped = credentials.reduce((acc, cred) => {
		// Credentials without uniqueIdentifier are standalone — no physical sibling possible
		const key = cred.uniqueIdentifier || `standalone-${cred.credentialID}`;

		if (!acc[key]) {
			acc[key] = { ...cred, integrations: [] };
		}

		// IR is always the primary record when present — it's the source of truth
		// for card number (CSN), name, and validity dates
		if (cred.integrationID === INTEGRATIONS_TYPES.INTEGRATION_INNER_RANGE.id) {
			acc[key] = { ...cred, integrations: acc[key].integrations };
		}

		acc[key].integrations.push({
			credentialID: cred.credentialID,
			integrationID: cred.integrationID
		});

		return acc;
	}, {});

	return Object.values(grouped);
}

/**
 * Specialized grouping for Access Cards Panel in Device Drawer.
 * Shows PEOPLE who have access to a specific device through their assigned credentials.
 * Pattern: Device -> Person (reverse of groupCardAccessBySourceForPerson)
 */
export function groupAccessCardsByPersonForDevice({ credentialAccess = [], credentials = [], people = [] }) {
	// Group access by person to avoid duplicates if they have multiple cards
	const grouped = {};

	credentialAccess
		.filter((ca) => ca.personID !== null)
		.forEach((ca) => {
			const key = `person-${ca.personID}`;
			if (!grouped[key]) {
				const person = people.find((p) => p.personID === ca.personID);

				grouped[key] = {
					personID: ca.personID,
					personName:
						person?.displayName ||
						`${person?.firstName || ''} ${person?.lastName || ''}`.trim() ||
						'Unknown Person',
					credentials: []
				};
			}

			const credential = credentials.find((c) => c.credentialID === ca.credentialID);
			if (credential) {
				grouped[key].credentials.push({
					credentialID: ca.credentialID,
					name: credential.name,
					value: credential.value
				});
			}
		});

	return Object.values(grouped)
		.map((item) => {
			const credentialNames = item.credentials.map((c) => c.name).join(', ');
			return {
				key: `person-${item.personID}`,
				personID: item.personID,
				entityTypeID: entityType.PERSON,
				entityID: item.personID,
				label: item.personName,
				tooltip: `Access via Card${item.credentials.length > 1 ? 's' : ''}: ${credentialNames}`,
				badgeColor: 'volcano',
				credentialsCount: item.credentials.length
			};
		})
		.sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Specialized grouping for Credentials Panel.
 * Enhances credentials with permission-based device access for tooltip display.
 * Permissions are the source of truth — a card is active on a device only if
 * the person has an active permission there.
 */
export function groupCredentialsBySourceForPerson({ credentials, devices = [], permissions = [] }) {
	return credentials.map((credential) => {
		const isPasscode = credential.credentialTypeID === CREDENTIAL_TYPE_PASSCODE;

		// All devices where the person has an active permission.
		const lockNames = credential.personID
			? permissions
					.filter((p) => p.personID === credential.personID && p.deviceID)
					.map((p) => devices.find((d) => d.deviceID === p.deviceID))
					.filter(Boolean)
					// Passcodes only apply to digital devices
					.filter((d) => !isPasscode || constants.ACTIONABLE_SMART_LOCKS.includes(d.defaultCategoryID))
					.map((d) => d.name)
			: [];

		const tooltip = lockNames.length > 0 ? 'Access to' : isPasscode ? 'No devices assigned' : undefined;

		return { ...credential, tooltip, lockNames };
	});
}

/**
 * Specialized grouping for the Device Credentials Panel.
 * Builds display items for credentials associated with a device via permissions.
 * Permissions are the source of truth — if a person has a permission on this device
 * and owns a credential, that credential is shown.
 */
export function groupCredentialsBySourceForDevice({ permissions = [], credentials = [], people = [] }) {
	// Get all people who have permissions on this device
	const personIDsWithAccess = [...new Set(permissions.filter((p) => p.personID).map((p) => p.personID))];

	// Find credentials owned by those people — deduplicate by credentialID
	const items = new Map();

	for (const personID of personIDsWithAccess) {
		const person = people.find((p) => p.personID === personID);
		const personCredentials = credentials.filter((c) => c.personID === personID);

		for (const c of personCredentials) {
			if (items.has(c.credentialID)) continue;

			items.set(c.credentialID, {
				key: `cr-${c.credentialID}`,
				credentialID: c.credentialID,
				name: c.name,
				value: c.value || '',
				credentialTypeID: c.credentialTypeID,
				tooltip: person
					? `Assigned to: ${person.displayName || `${person.firstName} ${person.lastName}`.trim()}`
					: null,
				integrationID: c.integrationID
			});
		}
	}

	return [...items.values()];
}

/**
 * Specialized grouping for the Device Permissions Panel.
 * Focuses on identifying all entities with access via PERMISSIONS only.
 *
 * Grouping logic:
 * - Direct access (no assetGroupID) → one badge per Person or Team
 *   Tooltip: "Direct access granted to this Person/Team"
 *
 * - Group-inherited access (assetGroupID present) → one badge per Device Group
 *   The Device Group is the root entity shown, not the individual Person/Team.
 *   Tooltip: lists all Teams and People that have access via this group.
 *
 * This makes the panel answer "what grants access to this device?" clearly.
 */
export function groupPermissionsBySourceForDevice({ permissions, people, teams, deviceGroups }) {
	// 🔐 Pre-filter permissions to only process entities within permitted scope
	const permittedPersonIDs = new Set(people.map((p) => p.personID));
	const permittedTeamIDs = new Set(teams.map((t) => t.teamID));
	const permittedGroupIDs = new Set(deviceGroups.map((g) => g.groupID));

	const scopedPermissions = permissions.filter((perm) => {
		if (perm.assetGroupID) return permittedGroupIDs.has(perm.assetGroupID);
		if (perm.teamID) return permittedTeamIDs.has(perm.teamID);
		if (perm.personID) return permittedPersonIDs.has(perm.personID);
		return false;
	});

	const directMap = {}; // key: `person-{id}` or `team-{id}`
	const groupMap = {}; // key: `group-{assetGroupID}`

	scopedPermissions.forEach((perm) => {
		const isTeam = !!perm.teamID;
		const entityID = isTeam ? perm.teamID : perm.personID;

		if (perm.assetGroupID) {
			// ── Group-inherited access → aggregate under the Device Group ──
			const groupKey = `group-${perm.assetGroupID}`;

			if (!groupMap[groupKey]) {
				groupMap[groupKey] = {
					assetGroupID: perm.assetGroupID,
					members: [] // { label, isTeam }
				};
			}

			// Collect the member name for the tooltip
			const person = !isTeam ? people.find((p) => p.personID === entityID) : null;
			const team = isTeam ? teams.find((t) => t.teamID === entityID) : null;
			const memberName = isTeam
				? team?.name || 'Unknown Team'
				: person?.displayName || person?.name || 'Unknown Person';

			// Avoid duplicates
			const alreadyAdded = groupMap[groupKey].members.some((m) => m.label === memberName && m.isTeam === isTeam);
			if (!alreadyAdded) {
				groupMap[groupKey].members.push({ label: memberName, isTeam });
			}
		} else {
			// ── Direct access → one badge per Person or Team ──
			const directKey = `${isTeam ? 'team' : 'person'}-${entityID}`;

			if (!directMap[directKey]) {
				const person = !isTeam ? people.find((p) => p.personID === entityID) : null;
				const team = isTeam ? teams.find((t) => t.teamID === entityID) : null;
				const entityName = isTeam
					? team?.name || 'Unknown Team'
					: person?.displayName || person?.name || 'Unknown Person';

				directMap[directKey] = {
					permissionID: perm.permissionID,
					entityTypeID: isTeam ? entityType.TEAM : entityType.PERSON,
					entityID,
					personID: perm.personID,
					teamID: perm.teamID,
					assetGroupID: null,
					label: entityName,
					tooltip: 'Direct',
					badgeColor: isTeam ? 'cyan' : 'purple',
					Icon: isTeam ? TeamIcon : UserIcon
				};
			}
		}
	});

	// ── Build group badges ────────────────────────────────────────────────────
	const groupBadges = Object.values(groupMap).map(({ assetGroupID }) => {
		const group = deviceGroups.find((g) => g.groupID === assetGroupID);
		const groupName = group?.name || 'Unknown Group';

		const tooltip = 'Via Group';

		return {
			permissionID: `group-${assetGroupID}`, // synthetic key for React
			entityTypeID: entityType.ASSET_GROUPS,
			entityID: assetGroupID,
			personID: null,
			teamID: null,
			assetGroupID,
			label: groupName,
			tooltip,
			badgeColor: 'blue',
			Icon: DeviceGroupsIcon // make sure this is imported where this function is used
		};
	});

	// ── Build direct badges ───────────────────────────────────────────────────
	const directBadges = Object.values(directMap);

	// ── Combine: direct first, then groups, sorted alphabetically within each ─
	const sortedDirect = directBadges.sort((a, b) => {
		if (a.entityTypeID !== b.entityTypeID) return a.entityTypeID - b.entityTypeID;
		return a.label.localeCompare(b.label);
	});

	const sortedGroups = groupBadges.sort((a, b) => a.label.localeCompare(b.label));

	return [...sortedDirect, ...sortedGroups];
}

/**
 * Specialized grouping for the Device Group Panel (Ticket #4681).
 * Identifies all entities (People/Teams) with access to this group
 * and preserves the source of that access.
 */
export function groupPermissionsByEntityForGroup({ permissions, people, teams }) {
	// 🔐 Pre-filter permissions to only process entities within permitted scope
	const permittedPersonIDs = new Set(people.map((p) => p.personID));
	const permittedTeamIDs = new Set(teams.map((t) => t.teamID));

	const scopedPermissions = permissions.filter((perm) => {
		if (perm.teamID) return permittedTeamIDs.has(perm.teamID);
		if (perm.personID) return permittedPersonIDs.has(perm.personID);
		return false;
	});

	const grouped = {};

	scopedPermissions.forEach((perm) => {
		// Determine the Entity type and ID
		const isTeam = perm.entityTypeID === entityType.TEAM;
		const entityID = isTeam ? perm.teamID : perm.personID;

		// 🔥 UNIQUE KEY: Combination of Entity + Access Source.
		// This ensures that redundant access paths (Direct vs Team) are visible for auditing.
		const key = `${perm.entityTypeID}-${entityID}-${isTeam ? 'team' : 'direct'}`;

		if (!grouped[key]) {
			grouped[key] = {
				permission: perm,
				entityID
			};
		}
	});

	return Object.values(grouped)
		.map(({ permission, entityID }) => {
			const isPerson = permission.entityTypeID === entityType.PERSON;
			const person = isPerson ? people.find((p) => p.personID === entityID) : null;
			const team = !isPerson ? teams.find((t) => t.teamID === entityID) : null;

			const entityName = isPerson
				? person?.displayName || person?.name || 'Unknown Person'
				: team?.name || 'Unknown Team';

			return {
				permissionID: permission.permissionID,
				entityTypeID: permission.entityTypeID,
				entityID: entityID,
				personID: permission.personID,
				teamID: permission.teamID,
				label: entityName,
				tooltip: isPerson ? 'Direct' : `Via ${entityName}`,
				badgeColor: isPerson ? 'purple' : 'cyan',
				Icon: isPerson ? UserIcon : TeamIcon,
				deviceNames: []
			};
		})
		.sort((a, b) => {
			// Sort numerically: PERSON (4) before TEAM (5)
			if (a.entityTypeID !== b.entityTypeID) return a.entityTypeID - b.entityTypeID;
			return a.label.localeCompare(b.label);
		});
}
